import { Result, Spin } from 'antd';
import { lazy, Suspense } from 'react';
import { Navigate, Route, Routes } from 'react-router-dom';
import { useAuth } from './auth/AuthContext';
import { PermissionRoute, ProtectedRoute } from './auth/ProtectedRoute';
import { adminMenu, type AdminMenuNode } from './config/menu';
import { MENU_PERMISSIONS as M, PERMISSIONS as P } from './config/permissions';
import { AdminLayout } from './layouts/AdminLayout';

const LoginPage = lazy(() => import('./pages/LoginPage').then((module) => ({ default: module.LoginPage })));
const DashboardPage = lazy(() => import('./pages/DashboardPage').then((module) => ({ default: module.DashboardPage })));
const MembersPage = lazy(() => import('./pages/MembersPage').then((module) => ({ default: module.MembersPage })));
const RolesPage = lazy(() => import('./pages/RolesPage').then((module) => ({ default: module.RolesPage })));
const RoleEditPage = lazy(() => import('./pages/RoleEditPage').then((module) => ({ default: module.RoleEditPage })));
const SiteSettingsPage = lazy(() => import('./pages/SiteSettingsPage').then((module) => ({ default: module.SiteSettingsPage })));
const HomePage = lazy(() => import('./pages/HomePage').then((module) => ({ default: module.HomePage })));
const ProductListingConfigPage = lazy(() => import('./pages/ProductListingConfigPage').then((module) => ({ default: module.ProductListingConfigPage })));
const ParameterTemplatePage = lazy(() => import('./pages/ParameterTemplatePage').then((module) => ({ default: module.ParameterTemplatePage })));
const ProductEditPage = lazy(() => import('./pages/ProductEditPage').then((module) => ({ default: module.ProductEditPage })));
const ProjectEditPage = lazy(() => import('./pages/ProjectEditPage').then((module) => ({ default: module.ProjectEditPage })));
const SolutionEditPage = lazy(() => import('./pages/SolutionEditPage').then((module) => ({ default: module.SolutionEditPage })));
const DocumentsPage = lazy(() => import('./pages/DocumentsPage').then((module) => ({ default: module.DocumentsPage })));
const FaqPage = lazy(() => import('./pages/FaqPage').then((module) => ({ default: module.FaqPage })));
const ContactPage = lazy(() => import('./pages/ContactPage').then((module) => ({ default: module.ContactPage })));
const InquiriesPage = lazy(() => import('./pages/InquiriesPage').then((module) => ({ default: module.InquiriesPage })));
const InquiryDetailPage = lazy(() => import('./pages/InquiryDetailPage').then((module) => ({ default: module.InquiryDetailPage })));
const MediaPage = lazy(() => import('./pages/MediaPage').then((module) => ({ default: module.MediaPage })));
const AuditLogsPage = lazy(() => import('./pages/AuditLogsPage').then((module) => ({ default: module.AuditLogsPage })));
const FixedPages = lazy(() => import('./pages/FixedPages'));
const ContentLists = lazy(() => import('./pages/ContentLists'));

const guard = (permission: string, element: React.ReactNode) => <PermissionRoute permission={permission}>{element}</PermissionRoute>;

function firstAccessiblePath(nodes: AdminMenuNode[], can: (permission?: string) => boolean): string | undefined {
  for (const node of nodes) {
    if (node.children) {
      const childPath = firstAccessiblePath(node.children, can);
      if (childPath) return childPath;
    }
    if (node.path && can(node.permission)) return node.path;
  }
  return undefined;
}

function DefaultRoute() {
  const { can } = useAuth();
  const path = firstAccessiblePath(adminMenu, can);
  return path
    ? <Navigate to={path} replace />
    : <Result status="403" title="暂无可访问菜单" subTitle="请联系管理员为当前账号分配成员角色。" />;
}

export function AppRoutes() {
  return <Suspense fallback={<div className="route-loader"><Spin size="large" /></div>}><Routes>
    <Route path="/login" element={<LoginPage />} />
    <Route element={<ProtectedRoute />}>
      <Route element={<AdminLayout />}>
        <Route index element={<DefaultRoute />} />
        <Route path="dashboard" element={guard(M.dashboard, <DashboardPage />)} />
        <Route path="members" element={guard(P.membersRead, <MembersPage />)} />
        <Route path="member-roles" element={guard(P.rolesManage, <RolesPage />)} />
        <Route path="member-roles/:id" element={guard(P.rolesManage, <RoleEditPage />)} />
        <Route path="site-settings" element={guard(M.siteSettings, <SiteSettingsPage />)} />
        <Route path="pages/home" element={guard(M.home, <HomePage />)} />
        <Route path="products/page" element={guard(M.products, <ProductListingConfigPage />)} />
        <Route path="products/parameter-template" element={guard(M.products, <ParameterTemplatePage />)} />
        <Route path="products/comparison-page" element={guard(M.products, <FixedPages pageKey="productComparison" title="产品对比页配置" comparison />)} />
        <Route path="products" element={guard(M.products, <ContentLists page="products" />)} />
        <Route path="products/:id" element={guard(M.products, <ProductEditPage />)} />
        <Route path="pages/design-yours" element={guard(M.designYours, <FixedPages page="design-yours" />)} />
        <Route path="projects/page" element={guard(M.projects, <FixedPages pageKey="projects" title="项目列表页配置" />)} />
        <Route path="projects" element={guard(M.projects, <ContentLists page="projects" />)} />
        <Route path="projects/:id" element={guard(M.projects, <ProjectEditPage />)} />
        <Route path="solutions/page" element={guard(M.solutions, <FixedPages pageKey="solutions" title="解决方案列表页配置" />)} />
        <Route path="solutions" element={guard(M.solutions, <ContentLists page="solutions" />)} />
        <Route path="solutions/:id" element={guard(M.solutions, <SolutionEditPage />)} />
        <Route path="pages/after-sales" element={guard(M.afterSales, <FixedPages page="after-sales" />)} />
        <Route path="quality-certifications" element={guard(M.quality, <DocumentsPage scope="CERTIFICATION" />)} />
        <Route path="pages/about-us" element={guard(M.aboutUs, <FixedPages page="about-us" />)} />
        <Route path="resources" element={guard(M.resources, <DocumentsPage scope="RESOURCE" />)} />
        <Route path="pages/process" element={guard(M.process, <FixedPages page="process" />)} />
        <Route path="faq" element={guard(M.faq, <FaqPage />)} />
        <Route path="contact/page" element={guard(M.contact, <ContactPage />)} />
        <Route path="inquiries" element={guard(M.contact, <InquiriesPage />)} />
        <Route path="inquiries/:id" element={guard(M.contact, <InquiryDetailPage />)} />
        <Route path="media" element={guard(M.media, <MediaPage />)} />
        <Route path="audit-logs" element={guard(P.auditRead, <AuditLogsPage />)} />
        <Route path="*" element={<Result status="404" title="404" subTitle="页面不存在" />} />
      </Route>
    </Route>
  </Routes></Suspense>;
}
